import React, { useState } from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import Helmet from 'react-helmet'
import { Link, Element } from 'react-scroll'
import Img from 'gatsby-image'
import Iframe from 'react-iframe'
import BackgroundImage from 'gatsby-background-image'
import { useTranslation } from 'react-i18next'
import { cover } from 'polished'
import clsx from 'clsx'

import { makeStyles, useTheme } from '@material-ui/core/styles'

import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'

import Button from '../components/Button'
import Section from '../components/Section'
import Heading from '../components/Heading'
import Hero from '../components/Hero'
import ShadowWrapper from '../components/ShadowWrapper'
import Team from '../components/Team'
import Gallery from '../components/Gallery'
import Slider from '../components/Slider'
import Map from '../components/Map'
import {
  defaultState,
  slides,
  draggedSpringOptions,
  trailingSpringOptions,
} from '../components/Slider/data'

import GatsbyLink from '../components/Link'

import pagesInfos from '../config/i18n/pagesInfos'

const offset = 30

const useStyles = makeStyles(theme => ({
  eventHeader: {
    height: '150px',
    padding: theme.spacing(2),
    backgroundPosition: 'top',
    borderRadius: '10px',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#fff',
    position: 'relative',
    textTransform: 'uppercase',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(3),
  },
  eventHeaderCover: {
    ...cover(),
    backgroundColor: theme.palette.primary.main,
    opacity: 0.6,
    zIndex: -1,
  },
  h3: {
    marginTop: theme.spacing(14),
    marginBottom: theme.spacing(6),
  },
  slider: {
    marginTop: theme.spacing(10),
    marginBottom: theme.spacing(6),
  },
  button: {
    marginTop: theme.spacing(8),
    marginBottom: theme.spacing(6),
  },
  iframe: {
    display: 'block',
    border: 0,
    height: '400px',
    boxShadow:
      '0 62.5px 125px -25px rgba(50, 50, 73, 0.5), 0 37.5px 75px -37.5px rgba(0, 0, 0, 0.6)',
  },
  imageWrapper: {
    position: 'relative',
    width: '100%',
    zIndex: 1,
    outline: '8px solid ' + theme.palette.primary.main,
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(8),
      marginBottom: theme.spacing(8),
    },
  },
  imageBackground: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    top: offset,
    bottom: offset,
    left: offset,
    right: offset,
    //borderRadius: '8px',
    zIndex: -1,

    border: 'solid 8px ' + theme.palette.secondary.main,
  },
}))

const ImplantologiePage = ({}) => {
  const { t, i18n } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const classes = useStyles()
  const [index, setIndex] = React.useState(0)
  const theme = useTheme()
  const data = useStaticQuery(graphql`
    query {
      bgCabinet: file(
        relativePath: {
          eq: "home/modern-metallic-dentist-tools-and-burnishers-on-a-6ZPKC39.jpg"
        }
      ) {
        childImageSharp {
          fluid(maxWidth: 1920) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      implantologie1: file(
        relativePath: { eq: "implantologie/implantologie1.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 488) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      implantologie2: file(
        relativePath: { eq: "implantologie/implantologie2.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 488) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      implantologie3: file(
        relativePath: { eq: "implantologie/implantologie3.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 488) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      implantologie4: file(
        relativePath: { eq: "implantologie/implantologie4.png" }
      ) {
        childImageSharp {
          fluid(maxWidth: 488) {
            ...GatsbyImageSharpFluid
          }
        }
      }
      implantologie5: file(
        relativePath: { eq: "implantologie/implantologie5.png" }
      ) {
        childImageSharp {
          fixed(width: 160) {
            ...GatsbyImageSharpFixed
          }
        }
      }
    }
  `)

  return (
    <React.Fragment>
      <Helmet
        title={t('implantologie:head.title')}
        meta={[
          {
            name: 'description',
            content: t('implantologie:head.description'),
          },
        ]}
      />
      <Element id="accueil" name="accueil">
        <Hero title={t('implantologie:title')} variant="secondary"></Hero>
      </Element>

      <Section bg="#fafdfd">
        <Grid container justify="space-between">
          <Grid item sm={12} xs={12}>
            {t('implantologie:section1.paragraphs1', {
              returnObjects: true,
            }).map(paragraph => (
              <Typography
                component="p"
                variant="body2"
                paragraph
                key={paragraph}
              >
                {paragraph}
              </Typography>
            ))}

            <ul>
              {t('implantologie:section1.list1', { returnObjects: true }).map(
                list => (
                  <Typography component="li" variant="body2" key={list}>
                    {list}
                  </Typography>
                )
              )}
            </ul>
            {t('implantologie:section1.paragraphs2', {
              returnObjects: true,
            }).map(paragraph => (
              <Typography
                component="p"
                variant="body2"
                paragraph
                key={paragraph}
              >
                {paragraph}
              </Typography>
            ))}
          </Grid>
        </Grid>
      </Section>
      <Section bg="#fff">
        <Heading
          title={t('implantologie:section2.title')}
          variant="secondary"
        />
        <Grid container justify="space-between" spacing={3}>
          <Grid item sm={12} xs={12}>
            <Typography component="p" variant="body2" paragraph>
              {t('implantologie:section2.caption1')}
            </Typography>
            <Img
              fluid={data.implantologie1.childImageSharp.fluid}
              style={{
                maxWidth: '488px',
              }}
              imgStyle={{
                objectFit: 'contain',
                height: 'auto',
              }}
              objectFit="contain"
            />
          </Grid>
          <Grid item sm={12} xs={12}>
            <Typography component="p" variant="body2" paragraph>
              {t('implantologie:section2.caption2')}
            </Typography>
            <Img
              fluid={data.implantologie2.childImageSharp.fluid}
              style={{
                maxWidth: '488px',
              }}
              imgStyle={{
                objectFit: 'contain',
                height: 'auto',
              }}
              objectFit="contain"
            />
          </Grid>
          <Grid item sm={12} xs={12}>
            <Img
              fluid={data.implantologie3.childImageSharp.fluid}
              style={{
                maxWidth: '488px',
              }}
              imgStyle={{
                objectFit: 'contain',
                height: 'auto',
              }}
              objectFit="contain"
            />
          </Grid>
        </Grid>
      </Section>
      <Section bg="#fafdfd">
        <Grid container justify="space-between" spacing={3}>
          <Grid item sm={12} xs={12}>
            <Heading
              title={t('implantologie:section3.title')}
              variant="secondary"
            />
            {t('implantologie:section3.paragraphs1', {
              returnObjects: true,
            }).map(paragraph => (
              <Typography
                component="p"
                variant="body2"
                paragraph
                key={paragraph}
              >
                {paragraph}
              </Typography>
            ))}
          </Grid>
        </Grid>
      </Section>
      <Section bg="#fff">
        <Grid container justify="space-between" spacing={3}>
          <Grid item sm={6} xs={12}>
            <Heading
              title={t('implantologie:section4.title')}
              variant="secondary"
            />
            {t('implantologie:section4.paragraphs1', {
              returnObjects: true,
            }).map(paragraph => (
              <Typography
                component="p"
                variant="body2"
                paragraph
                key={paragraph}
              >
                {paragraph}
              </Typography>
            ))}
          </Grid>
          <Grid item sm={6} xs={12}>
            <Img
              fluid={data.implantologie4.childImageSharp.fluid}
              objectFit="cover"
              objectPosition="50% 50%"
            />
          </Grid>
        </Grid>
      </Section>
      <Section bg="#fff">
        <Grid container justify="space-between" spacing={3}>
          <Grid item sm={12} xs={12}>
            <Heading
              title={t('implantologie:section5.title')}
              variant="secondary"
            />
            {t('implantologie:section5.paragraphs1', {
              returnObjects: true,
            }).map(paragraph => (
              <Typography
                component="p"
                variant="body2"
                paragraph
                key={paragraph}
              >
                {paragraph}
              </Typography>
            ))}
          </Grid>
          <Grid item sm={12} xs={12}>
            <Heading
              title={t('implantologie:section6.title')}
              variant="secondary"
            />
            {t('implantologie:section6.paragraphs1', {
              returnObjects: true,
            }).map(paragraph => (
              <Typography
                component="p"
                variant="body2"
                paragraph
                key={paragraph}
              >
                {paragraph}
              </Typography>
            ))}
          </Grid>
          <Grid item sm={12} xs={12}>
            <Heading
              title={t('implantologie:section7.title')}
              variant="secondary"
            />
            {t('implantologie:section7.paragraphs1', {
              returnObjects: true,
            }).map(paragraph => (
              <Typography
                component="p"
                variant="body2"
                paragraph
                key={paragraph}
              >
                {paragraph}
              </Typography>
            ))}
            <ul>
              {t('implantologie:section7.list1', {
                returnObjects: true,
              }).map(list => (
                <Typography component="li" variant="body2" key={list}>
                  {list}
                </Typography>
              ))}
            </ul>
          </Grid>
          <Grid item sm={12} xs={12}>
            <Heading
              title={t('implantologie:section8.title')}
              variant="secondary"
            />

            {t('implantologie:section8.paragraphs1', {
              returnObjects: true,
            }).map(paragraph => (
              <Typography
                component="p"
                variant="body2"
                paragraph
                key={paragraph}
              >
                {paragraph}
              </Typography>
            ))}
          </Grid>
        </Grid>
      </Section>
      <Section bg="#fff">
        <Grid container justify="space-between" spacing={3}>
          <Grid item sm={9} xs={12}>
            <Heading
              title={t('implantologie:section9.title')}
              variant="secondary"
            />
            {t('implantologie:section9.paragraphs1', {
              returnObjects: true,
            }).map(paragraph => (
              <Typography
                component="p"
                variant="body2"
                paragraph
                key={paragraph}
              >
                {paragraph}
              </Typography>
            ))}
          </Grid>
          <Grid item sm={3} xs={12} container justify="center">
            <Img fixed={data.implantologie5.childImageSharp.fixed} />
          </Grid>
          <Grid item sm={12} xs={12}>
            {t('implantologie:section9.paragraphs2', {
              returnObjects: true,
            }).map(paragraph => (
              <Typography
                component="p"
                variant="body2"
                paragraph
                key={paragraph}
              >
                {paragraph.startsWith('http') ? (
                  <a
                    href={paragraph}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {paragraph}
                  </a>
                ) : (
                  paragraph
                )}
              </Typography>
            ))}
          </Grid>
        </Grid>
      </Section>
      <Section bg={theme.palette.tertiary.light}>
        <Grid container justify="space-between" spacing={3}>
          <Grid item sm={9} xs={12}>
            <Heading
              title={t('implantologie:section10.title')}
              variant="secondary"
            />
            {t('implantologie:section10.paragraphs1', {
              returnObjects: true,
            }).map(paragraph => (
              <Typography
                component="p"
                variant="body2"
                paragraph
                key={paragraph}
              >
                {paragraph}
              </Typography>
            ))}
          </Grid>
          <Grid item sm={12} xs={12} container justify="center">
            <Button
              href="https://storage.googleapis.com/innobelge-websites.appspot.com/sites/crucifix/Focus_implants_brochure%20SMD.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('implantologie:section10.button')}
            </Button>
          </Grid>
        </Grid>
      </Section>
    </React.Fragment>
  )
}

ImplantologiePage.defaultProps = {}

export default ImplantologiePage
